(ns admin.events.resources
  (:require [applied-science.js-interop :as j]
            [kyber.events.utils :refer [add-auth base-uri kyber-event-fx]]
            [admin.db.activity :as db-activity]
            [kyber.events.eql :as eql]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-resources
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resources-success
    {:description "Get resources success"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-resources-success
  [{:keys [db]} [event-name response]]
  {:db (assoc db :resources response)
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :resources/get-resources-success get-resources-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resources-failure
    {:description "Get resources failure"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-resources-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :resources/get-resources-failure get-resources-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resources
    {:description "Get resources request"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-resources
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request {:name event-name
             :method :get
             :uri (base-uri db "/v1/admin/resources")
             :response-format :json
             :headers (add-auth db)
             :on-success [:resources/get-resources-success]
             :on-failure [:resources/get-resources-failure]}})

(kyber-event-fx :resources/get-resources get-resources)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Link S3 Resource
;;
;; File Upload Step 3
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-link-s3-resource-success
    {:description "Linked an uploaded resource to a deployment"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn link-s3-resource-success
  [{:keys [db]} [_event-name upload-state resource]]
  (let [{:keys [on-success]} @upload-state]
    (cond-> {:db (update db :resources conj resource)}
      (some? on-success)
      (assoc :invoke #(do
                        (swap! upload-state assoc :is-uploading false)
                        (on-success resource
                                    (dissoc @upload-state :on-success)))))))

(kyber-event-fx :resources/link-s3-resource-success
                link-s3-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-link-s3-resource-failure
    {:description "Failed to link an uploaded resource to a deployment"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn link-s3-resource-failure
  [{:keys [db]} [_event-name upload-state _err]]
  {:db db
   :dispatch-n [[:notifier/create-toast
                 {:message "Resource failed to upload"
                  :status :error}]]
   :invoke #(swap! upload-state assoc :is-uploading false)})

(kyber-event-fx :resources/link-s3-resource-failure
                link-s3-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-link-s3-resource
    {:description "Request to link an uploaded resource to a deployment"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn link-s3-resource
  [{:keys [db]} [event-name upload-state file-key]]
  (let [{original-file :original-file
         public-access :public-access
         file-name :name
         description :description
         :or {public-access false}}
        @upload-state]

    {:db db
     :request
     {:name event-name
      :method :post
      :uri (base-uri db (str "/v2/admin/resources"))
      :params (cond-> {:file-name (.-name original-file)
                       :file-size (.-size original-file)
                       :file-type (.-type original-file)
                       :file-key file-key
                       :public public-access}
                (seq file-name)
                (assoc :name file-name)

                (seq description)
                (assoc :description description))
      :format :json
      :response-format :json
      :headers (add-auth db)
      :on-success [:resources/link-s3-resource-success upload-state]
      :on-failure [:resources/link-s3-resource-failure upload-state]}}))

(kyber-event-fx :resources/link-s3-resource
                link-s3-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; upload-plo-resource-to-s3
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-plo-resource-to-s3-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn upload-plo-resource-to-s3-success
  [{:keys [db]} [event-name props]]
  (let [{:keys [upload-state curriculum-uuid activity-uuid
                primary-learning-object on-s3-upload-success]}
        props

        {:resource/keys [status uuid] :as resource}
        primary-learning-object]
    (cond-> {:db (-> db
                     (update :resources conj {:uuid uuid
                                              :status status
                                              :processing? true})
                     (assoc-in [:curricula
                                curriculum-uuid
                                :activities
                                activity-uuid
                                :primary-learning-object] resource))
             :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]}
      (fn? on-s3-upload-success)
      (assoc :invoke #(do
                        (swap! upload-state assoc :is-uploading false)
                        (on-s3-upload-success primary-learning-object))))))

(kyber-event-fx :resources/upload-plo-resource-to-s3-success
                upload-plo-resource-to-s3-success)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; upload-resource-to-s3
;;
;; File Upload Step 2
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-to-s3-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn upload-resource-to-s3-success
  [{:keys [db]} [event-name props]]
  (let [{:keys [dispatch-on-s3-upload-success on-resource-uploaded]} props]
    (cond-> {:db db
             :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]}
      (keyword? dispatch-on-s3-upload-success)
      (update :dispatch-n conj [dispatch-on-s3-upload-success props])

      (fn? on-resource-uploaded)
      (assoc :invoke #(on-resource-uploaded props)))))

(kyber-event-fx :resources/upload-resource-to-s3-success
                upload-resource-to-s3-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-to-s3-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn upload-resource-to-s3-failure
  [{:keys [db]} [event-name upload-state _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast
                 {:message "Resource failed to upload"
                  :status :error}]]
   :invoke #(swap! upload-state assoc :is-uploading false)})

(kyber-event-fx :resources/upload-resource-to-s3-failure
                upload-resource-to-s3-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-to-s3-abort
    {:description ""
     :changelog
     [{:date "30SEP21"
       :event-version 0
       :data {}}]}))

(defn upload-resource-to-s3-abort
  [{:keys [db]} [event-name upload-state]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast
                 {:message "Resource upload canceled"}]]
   :invoke #(swap! upload-state assoc :is-uploading false)})

(kyber-event-fx :resources/upload-resource-to-s3-abort
                upload-resource-to-s3-abort)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-to-s3-progress
    {:description "Progress event while uploading to s3"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn upload-resource-to-s3-progress
  [{:keys [db]} [event-name upload-state progress]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint
                         {:event event-name
                          :event-version 0}]]}
    (some? progress)
    (assoc :invoke #(swap! upload-state assoc :percent-uploaded
                           (* (/ (.-loaded progress)
                                 (.-total progress))
                              100)))))

(kyber-event-fx :resources/upload-resource-to-s3-progress
                upload-resource-to-s3-progress)

(defn upload-resource-to-s3
  [{:keys [db]} [event-name {:keys [upload-state presigned-url on-success-handler] :as props}]]
  (let [{:keys [original-file content-disposition] :or {content-disposition :attachment}}
        @upload-state]
    {:db db
     :request
     {:name event-name
      :method :put
      :uri presigned-url
      :response-format :text
      :body original-file
      ;; Note: Auth is in presigned-url
      :headers {"Content-Type" (.-type original-file)
                "Content-Disposition" (name content-disposition)}

      :on-progress [:resources/upload-resource-to-s3-progress upload-state]
      :on-success [on-success-handler props]
      :on-abort [:resources/upload-resource-to-s3-abort upload-state]
      :on-failure [:resources/upload-resource-to-s3-failure upload-state]}}))

(kyber-event-fx :resources/upload-resource-to-s3
                upload-resource-to-s3)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-file-upload-key
;;
;; File Upload Step 1
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-file-upload-key-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-file-upload-key-success
  [{:keys [db]} [event-name props {:keys [presigned-url key]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:resources/upload-resource-to-s3
                 (assoc props
                        :presigned-url presigned-url
                        :resource-key key)]]})

(kyber-event-fx :resources/get-file-upload-key-success
                get-file-upload-key-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-file-upload-key-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-file-upload-key-failure
  [{:keys [db]} [event-name _ _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast {:message "Failed to upload."
                                         :status :error}]]})

(kyber-event-fx :resources/get-file-upload-key-failure
                get-file-upload-key-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-file-upload-key
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-file-upload-key
  [{:keys [db]} [event-name upload-state]]
  (let [contains-file (-> @upload-state :original-file some?)
        upload-has-not-begun (-> @upload-state :percent-uploaded zero?)]
    (cond->
        {:db db
         :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                  :event-version 0}]]}
      (and (true? contains-file)
           (true? upload-has-not-begun))
      (assoc :request
             {:name event-name
              :method :get
              :uri (base-uri db (str "/v2/admin/file-upload"))
              :format :json
              :response-format :json
              :headers (add-auth db)
              :on-success [:resources/get-file-upload-key-success
                           {:upload-state upload-state
                            :on-success :resources/upload-resource-to-s3-success}]
              :on-failure [:resources/get-file-upload-key-failure
                           upload-state]})

      (some? (@upload-state :disposition))
      (assoc-in [:request :params]
                {:disposition (name (:disposition @upload-state))}))))

(kyber-event-fx :resources/get-file-upload-key get-file-upload-key)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-plo-file-upload-key
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-plo-file-upload-key-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-plo-file-upload-key-success
  [{:keys [db]} [event-name props {activity 'activity/upload-resource}]]
  (let [{:activity/keys [presigned-url primary-learning-object]}
        activity]
    {:db db
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]
                  [:resources/upload-resource-to-s3
                   (assoc props
                          :presigned-url presigned-url
                          :on-success-handler :resources/upload-plo-resource-to-s3-success
                          :primary-learning-object primary-learning-object)]]}))

(kyber-event-fx ::get-plo-file-upload-key-success
                get-plo-file-upload-key-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-plo-file-upload-key-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-plo-file-upload-key-failure
  [{:keys [db]} [event-name _ _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast {:message "Failed to upload."
                                         :status :error}]]})

(kyber-event-fx ::get-plo-file-upload-key-failure
                get-plo-file-upload-key-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-plo-file-upload-key
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-plo-file-upload-key
  [{:keys [db]} [event-name {:keys [upload-state activity-uuid] :as props}]]
  (let [file (:original-file @upload-state)
        upload-has-not-begun (-> @upload-state :percent-uploaded zero?)]

    (cond->
     {:db db
      :dispatch-n [[:analytics/save-datapoint {:event event-name
                                               :event-version 0}]]}
      (and (some? file)
           (true? upload-has-not-begun))
      (assoc :request
             (eql/request db
                          {:name event-name
                           :params (db-activity/eql-upload-resource
                                    {:uuid activity-uuid
                                     :primary-learning-object
                                     {:resource/type (j/get file :type)
                                      :resource/content-disposition "attachment"
                                      :resource/file-name (j/get file :name)
                                      :resource/is-public false}}
                                    [:uuid
                                     :presigned-url
                                     {:activity/primary-learning-object
                                      [:resource/uuid :resource/status]}])
                           :on-success [::get-plo-file-upload-key-success props]
                           :on-failure [::get-plo-file-upload-key-failure upload-state]})))))

(kyber-event-fx ::get-plo-file-upload-key get-plo-file-upload-key)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-activity-file-upload-key
;;
;; File Upload Step 1 (Activity Linked File)
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-file-upload-key-success
    {:description ""
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn get-activity-file-upload-key-success
  [{:keys [db]} [event-name upload-context response]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:resources/upload-activity-resource-to-s3
                 (merge upload-context response)]]})

(kyber-event-fx :resources/get-activity-file-upload-key-success
                get-activity-file-upload-key-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-file-upload-key-failure
    {:description ""
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn get-activity-file-upload-key-failure
  [{:keys [db]} [event-name _ _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast {:message "Failed to upload."
                                         :status :error}]]})

(kyber-event-fx :resources/get-activity-file-upload-key-failure
                get-activity-file-upload-key-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-file-upload-key
    {:description ""
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn get-activity-file-upload-key
  [{:keys [db]} [event-name {:keys [upload-state activity-uuid]}]]
  (let [{:keys [public-access original-file]} @upload-state
        contains-file (some? original-file)
        upload-has-not-begun (-> @upload-state :percent-uploaded zero?)]
    (cond->
        {:db db
         :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                  :event-version 0}]]}
      (and (true? contains-file)
           (true? upload-has-not-begun))
      (assoc :request
             {:name event-name
              :method :get
              :uri (base-uri db (str "/v2/admin/activities/" activity-uuid
                                     "/resources/upload-url"))
              :params (cond-> {:file-name (.-name original-file)}
                        (true? public-access)
                        (assoc :public public-access))
              :format :json
              :response-format :json
              :headers (add-auth db)
              :on-success [:resources/get-activity-file-upload-key-success
                           {:upload-state upload-state
                            :activity-uuid activity-uuid}]
              :on-failure [:resources/get-activity-file-upload-key-failure
                           upload-state]})

      (some? (@upload-state :disposition))
      (assoc-in [:request :params :disposition]
                (name (:disposition @upload-state))))))

(kyber-event-fx :resources/get-activity-file-upload-key
                get-activity-file-upload-key)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; upload-activity-resource-to-s3
;;
;; File Upload Step 2 (Activity Linked File)
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource-to-s3-success
    {:description ""
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource-to-s3-success
  [{:keys [db]} [event-name upload-context _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:resources/link-resource-to-activity
                 upload-context]]})

(kyber-event-fx :resources/upload-activity-resource-to-s3-success
                upload-activity-resource-to-s3-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource-to-s3-failure
    {:description ""
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource-to-s3-failure
  [{:keys [db]} [event-name upload-state _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint
                 {:event event-name
                  :event-version 0}]
                [:notifier/create-toast
                 {:message "Resource failed to upload"
                  :status :error}]]
   :invoke #(swap! upload-state assoc :is-uploading false)})

(kyber-event-fx :resources/upload-activity-resource-to-s3-failure
                upload-activity-resource-to-s3-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource-to-s3-progress
    {:description "Progress event while uploading to s3"
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource-to-s3-progress
  [{:keys [db]} [event-name upload-state progress]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint
                         {:event event-name
                          :event-version 0}]]}
    (some? progress)
    (assoc :invoke #(swap! upload-state assoc :percent-uploaded
                           (* (/ (.-loaded progress)
                                 (.-total progress))
                              100)))))

(kyber-event-fx :resources/upload-activity-resource-to-s3-progress
                upload-activity-resource-to-s3-progress)

(defn upload-activity-resource-to-s3
  [{:keys [db]} [event-name
                 {:keys [upload-state presigned-url] :as upload-context}
                 _]]
  (let [{:keys [original-file disposition] :or {disposition :attachment}}
        @upload-state]

    {:db db
     :request
     {:name event-name
      :method :put
      :uri presigned-url
      :response-format :text
      :body original-file
      ;; Note: Auth is in presigned-url
      :headers {"Content-Type" (.-type original-file)
                "Content-Disposition" (name disposition)}

      :on-progress [:resources/upload-activity-resource-to-s3-progress
                    upload-state]
      :on-success [:resources/upload-activity-resource-to-s3-success
                   upload-context]
      :on-failure [:resources/upload-activity-resource-to-s3-failure
                   upload-state]}}))

(kyber-event-fx :resources/upload-activity-resource-to-s3
                upload-activity-resource-to-s3)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; link-resource-to-activity
;;
;; File Upload Step 3 (Activity Linked File)
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-link-resource-to-activity-success
    {:description "Linked an uploaded resource to a deployment"
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn link-resource-to-activity-success
  [{:keys [db]} [_event-name {:keys [upload-state]} resource]]
  (let [{:keys [on-success]} @upload-state]
    (cond-> {:db (update db :resources conj resource)}
      (some? on-success)
      (assoc :invoke #(do
                        (swap! upload-state assoc :is-uploading false)
                        (on-success resource))))))

(kyber-event-fx :resources/link-resource-to-activity-success
                link-resource-to-activity-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-link-resource-to-activity-failure
    {:description "Failed to link an uploaded resource to a deployment"
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn link-resource-to-activity-failure
  [{:keys [db]} [_event-name upload-state _err]]
  {:db db
   :dispatch-n [[:notifier/create-toast
                 {:message "Resource failed to upload"
                  :status :error}]]
   :invoke #(swap! upload-state assoc :is-uploading false)})

(kyber-event-fx :resources/link-resource-to-activity-failure
                link-resource-to-activity-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-link-resource-to-activity
    {:description "Request to link an uploaded resource to a deployment"
     :changelog
     [{:date "30JUL19"
       :event-version 0
       :data {}}]}))

(defn link-resource-to-activity
  [{:keys [db]} [event-name {:keys [upload-state
                                    resource-upload-signature
                                    resource-uuid
                                    activity-uuid]
                             :as upload-context}]]
  (let [{original-file :original-file
         public-access :public-access
         file-name :name
         description :description
         :or {public-access false}}
        @upload-state]
    {:db db
     :request
     {:name event-name
      :method :put
      :uri (base-uri db (str "/v2/admin/activities/" activity-uuid
                             "/resources/" resource-uuid))
      :params (cond-> {:resource-upload-signature resource-upload-signature
                       :file-name (.-name original-file)
                       :file-size (.-size original-file)
                       :file-type (.-type original-file)
                       :public public-access}
                (seq file-name)
                (assoc :name file-name)

                (seq description)
                (assoc :description description))
      :format :json
      :response-format :json
      :headers (add-auth db)
      :on-success [:resources/link-resource-to-activity-success
                   upload-context]
      :on-failure [:resources/link-resource-to-activity-failure
                   upload-state]}}))

(kyber-event-fx :resources/link-resource-to-activity
                link-resource-to-activity)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resource-success
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn get-resource-success
  [{:keys [_db]} [_ props response]]
  (let [{:keys [on-resource-created redispatch-when wait]} props
        wait* (* 2 (or wait 1000))
        props* (assoc props :wait wait*)
        resource (:>/resource response)
        should-redispatch (boolean (redispatch-when props resource))]
    (cond-> {}
      (true? should-redispatch)
      (assoc :dispatch-later [{:ms wait*
                               :dispatch [::get-resource props*]}])

      (and (false? should-redispatch)
           (fn? on-resource-created))
      (assoc :invoke #(on-resource-created resource)))))

(kyber-event-fx ::get-resource-success get-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resource-failure
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn get-resource-failure
  [_ [event-name _ _]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::get-resource-failure get-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-resource
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn get-resource
  [{:keys [db]} [event-name {:keys [resource] :as props}]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request (eql/request
             db
             {:name event-name
              :params `[{(:>/resource {:resource/uuid ~(:resource/uuid resource)})
                         [:resource/uuid
                          :resource/created-at
                          :resource/created-by
                          :resource/download-key
                          :resource/download-path
                          :resource/failed?
                          :resource/file-name
                          :resource/file-size
                          :resource/file-type
                          :resource/name
                          :resource/processing?
                          :resource/source-key
                          :resource/source-path
                          :resource/status
                          :resource/url
                          :resource/m3u8
                          :resource/viewer-key
                          :resource/viewer-path]}]
              :on-success [::get-resource-success props]
              :on-failure [::get-resource-failure props]})})

(kyber-event-fx ::get-resource get-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; upload-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-success
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-resource-success
  [{:keys [_db]} [event-name props {resource 'resource/upload}]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:resources/upload-resource-to-s3
                 (assoc props
                        :presigned-url (:resource/presigned-url resource)
                        :on-success-handler :resources/upload-resource-to-s3-success
                        :dispatch-on-s3-upload-success ::get-resource
                        :resource resource)]]})

(kyber-event-fx ::upload-resource-success upload-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource-failure
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-resource-failure
  [_ [event-name _ _]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str "Failed to upload resource. Please try again")
                  :status :error}]]})

(kyber-event-fx ::upload-resource-failure upload-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-resource
    {:description ""
     :changelog
     [{:date "28JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-resource
  [{:keys [db]} [event-name {:keys [upload-state] :as raw-props}]]
  (let [props (assoc raw-props
                     :redispatch-when
                     #(nil? (:resource/viewer-key %2)))
        file (:original-file @upload-state)
        upload-has-not-begun (-> @upload-state :percent-uploaded zero?)
        content-disposition (or (@upload-state :content-disposition)
                                "attachment")]

    (cond-> {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]}
      (and (some? file)
           (true? upload-has-not-begun))
      (assoc :request
             (eql/request
              db
              {:name event-name
               :params `[{(resource/upload
                           {:resource/type ~(j/get file :type)
                            :resource/content-disposition ~content-disposition
                            :resource/file-name ~(j/get file :name)
                            :resource/is-public ~(:public-access @upload-state)})
                          [:resource/uuid
                           :resource/presigned-url]}]
               :on-success [::upload-resource-success props]
               :on-failure [::upload-resource-failure props]})))))

(kyber-event-fx ::upload-resource upload-resource)
